import { useQuery } from '@tanstack/react-query';

import { getClassPackCodes } from '@/services/class-pack-code';
import type { CodeGuestPasses } from '@/helpers/guest-pass';
import { ClassPack } from './class-pack-groups';

export interface ClassPackCode {
  admin_user_id: string;
  class_pack: ClassPack;
  class_pack_code_id: string;
  class_pack_group_id: string;
  class_pack_id: string;
  classes_left: number;
  // Absent on a backend without waitlists: read it through getHeldCredits.
  credits_held?: number;
  code: string;
  created_at: string;
  expires_at: string | null;
  expiry_days: number | null;
  guest_passes?: CodeGuestPasses | null;
  status: 'ACTIVE' | 'INACTIVE' | 'EXPIRED' | 'FULLY_USED';
  tenant_id: string;
  type: 'CUSTOM' | 'PAID';
  // A pack minted by redeeming a classes gift card.
  from_gift_card?: boolean;
  updated_at: string;
  user_id: string;
  user: {
    first_name: string;
    last_name: string | null;
    user_id: string;
  };
  class_pack_order_item?: {
    class_pack_order_item_id: string;
    order_id: string;
    original_price: number;
    discounted_price: number;
    currency: string;
  };
}

export default function useClassPackCodes({
  userId,
  email,
}: { userId?: string; email?: string } = {}) {
  const { data, isLoading, isSuccess, error, refetch } = useQuery({
    queryKey: ['classPackCodes', userId, email],
    queryFn: () => getClassPackCodes({ user_id: userId, email }),
    enabled: !!userId || !!email,
    refetchOnMount: true,
  });

  return {
    data,
    isLoading,
    // A 2xx whose body lacked the expected key resolves with no data and no
    // error, so an unreadable answer has to be caught alongside a rejected one.
    hasFailedRead: !!error || (isSuccess && !data),
    error,
    refetch,
  };
}
